import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { journeyCatalog } from "@seeing-maths/content-runtime";
import type { JourneyCatalogEntry } from "@seeing-maths/contracts";
import { useLearning } from "../learning/learning-context";

function stateFor(
  entry: JourneyCatalogEntry,
  visibleState: string | undefined,
): { label: string; className: string } {
  if (entry.implementation !== "live") {
    return { label: "Path forming", className: "map-pending" };
  }
  if (visibleState === "remembered") return { label: "Remembered", className: "map-remembered" };
  if (visibleState === "strong") return { label: "Mapped", className: "map-strong" };
  if (visibleState === "working" || visibleState === "discovered") {
    return { label: "In progress", className: "map-working" };
  }
  return { label: "Open", className: "map-open" };
}

export function AtlasPage() {
  const { projection } = useLearning();
  const [selectedId, setSelectedId] = useState("ALG-001");
  const selected = useMemo(
    () => journeyCatalog.find((entry) => entry.id === selectedId) ?? journeyCatalog[0],
    [selectedId],
  );
  const selectedProjection = projection?.concepts[selected.id];
  const selectedState = stateFor(selected, selectedProjection?.visibleState);
  const resumeStage = selectedProjection?.resumeCheckpoint?.stage ?? "notice";
  const firstTrail = journeyCatalog.filter((entry) => entry.region === "first-trail");
  const horizons = journeyCatalog.filter((entry) => entry.region === "new-horizons");

  return (
    <main className="atlas-page">
      <section className="atlas-intro">
        <div>
          <p className="page-kicker">THE ATLAS</p>
          <h1>Follow the relationships.</h1>
          <p>
            Every place begins with something you can see and change. The language comes later.
          </p>
        </div>
        <div className="atlas-note">
          <span className="tag tag-sage">First path open</span>
          <p>Begin in the Hidden Grove. More paths will appear as the Atlas grows.</p>
        </div>
      </section>

      <div className="atlas-layout">
        <section className="route-card card" aria-labelledby="first-trail-title">
          <div className="route-card-heading">
            <div>
              <p className="card-kicker">THE FIRST TRAIL</p>
              <h2 id="first-trail-title">Six connected places</h2>
            </div>
            <span className="route-summary">1 open · 5 forming</span>
          </div>
          <div className="route-grid first-route">
            {firstTrail.map((entry) => {
              const state = stateFor(entry, projection?.concepts[entry.id]?.visibleState);
              return (
                <button
                  type="button"
                  key={entry.id}
                  className={`route-node ${state.className}`}
                  aria-pressed={selectedId === entry.id}
                  aria-label={`Journey ${entry.number}, ${entry.title}, ${state.label}`}
                  onClick={() => setSelectedId(entry.id)}
                >
                  <span className="route-node-glyph">{entry.glyph}</span>
                  <span className="route-node-number">{String(entry.number).padStart(2, "0")}</span>
                  <strong>{entry.title}</strong>
                  <small>{state.label}</small>
                </button>
              );
            })}
          </div>

          <div className="route-divider" />

          <div className="route-card-heading route-card-heading-small">
            <div>
              <p className="card-kicker">NEW HORIZONS</p>
              <h2>Journeys 07–12</h2>
            </div>
            <span className="route-summary">Paths still forming</span>
          </div>
          <div className="route-grid horizon-route">
            {horizons.map((entry) => {
              const state = stateFor(entry, projection?.concepts[entry.id]?.visibleState);
              return (
                <button
                  type="button"
                  key={entry.id}
                  className={`route-node ${state.className}`}
                  aria-pressed={selectedId === entry.id}
                  aria-label={`Journey ${entry.number}, ${entry.title}, ${state.label}`}
                  onClick={() => setSelectedId(entry.id)}
                >
                  <span className="route-node-glyph">{entry.glyph}</span>
                  <span className="route-node-number">{String(entry.number).padStart(2, "0")}</span>
                  <strong>{entry.title}</strong>
                  <small>{state.label}</small>
                </button>
              );
            })}
          </div>
        </section>

        <aside className="journey-detail card" aria-live="polite">
          <div className="detail-heading">
            <span className={`detail-glyph ${selectedState.className}`} aria-hidden="true">
              {selected.glyph}
            </span>
            <div>
              <p className="card-kicker">JOURNEY {String(selected.number).padStart(2, "0")} · {selected.world}</p>
              <h2>{selected.title}</h2>
            </div>
          </div>
          <p className="journey-summary">{selected.summary}</p>
          <div className="detail-question">
            <span>Current state</span>
            <strong>{selectedState.label}</strong>
          </div>
          {selected.implementation === "live" ? (
            <Link className="btn btn-primary detail-primary" to={`/journeys/${selected.id}/${resumeStage}`}>
              {selectedProjection ? "Continue journey" : "Begin journey"}
            </Link>
          ) : (
            <div className="migration-note">
              <strong>This path is still forming</strong>
              <p>Its artifacts are not ready to enter yet. Begin with an open path and return later.</p>
            </div>
          )}
          <div className="map-legend" aria-label="Route state legend">
            <span><i className="legend-dot legend-open" /> open</span>
            <span><i className="legend-dot legend-working" /> in progress</span>
            <span><i className="legend-dot legend-pending" /> forming</span>
          </div>
        </aside>
      </div>
    </main>
  );
}
